/*-------------Libraries-----------------*/
import { makeAutoObservable, reaction, runInAction, observable } from "mobx";

/*---------------API Client-----------------------*/
import agent from "../api/agent";
import { store } from "./store";

/*-------------Models--------------------*/
import { CustomerDetails } from "../models/Customer";
import { SystemUser } from "../models/SystemUser";

export default class CommonStore {
  customer: CustomerDetails | null = null;
  user: SystemUser | null = null;
  token: string | null = localStorage.getItem("jwt");
  refreshToken: string | null = localStorage.getItem("refreshToken");
  warehouseId: number | null = null;
  availableDeliveryTypes: number[] = [];
  loadingCustomerDetail: boolean = false;
  loadingWarehouse: boolean = false;
  customerTypeId: number | null = null;
  menuId: number | null = localStorage.getItem("menuId")
    ? Number(localStorage.getItem("menuId"))
    : null;
  isDirectDelivery: boolean | null = localStorage.getItem("isDirectDelivery")
    ? localStorage.getItem("isDirectDelivery") === "true"
    : null;

  get actualAvailableDeliveryTypes() {
    if (this.isDirectDelivery) {
      const types = this.availableDeliveryTypes.filter((type) => type !== 2);
      return types.length > 0 ? types : [1];
    }
    return this.availableDeliveryTypes;
  }


  constructor() {
    makeAutoObservable(this, {
      workbox: observable.ref,
    });

    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        this.user = JSON.parse(storedUser);
      } catch (error) {
        console.error("Error parsing stored user:", error);
        localStorage.removeItem("user");
      }
    }

    // Load customerTypeId from localStorage on initialization
    const storedCustomerTypeId = localStorage.getItem("customerTypeId");
    if (storedCustomerTypeId) {
      this.customerTypeId = Number(storedCustomerTypeId);
    }

    // Token reaction - handles token changes
    reaction(
      () => this.token,
      (token) => {
        if (token) {
          localStorage.setItem("jwt", token);
          if (this.user) {
            localStorage.setItem("user", JSON.stringify(this.user));
          }
        } else {
          // Clear all auth-related data when token is removed
          localStorage.removeItem("jwt");
          localStorage.removeItem("user");
          localStorage.removeItem("refreshToken");
          localStorage.removeItem("customerTypeId");
          localStorage.removeItem("id");

          // Reset store state
          this.refreshToken = null;
          this.customerTypeId = null;
          this.user = null;
          this.customer = null;
        }
      },
    );

    // Refresh token reaction - handles refresh token changes
    reaction(
      () => this.refreshToken,
      (refreshToken) => {
        if (refreshToken) {
          localStorage.setItem("refreshToken", refreshToken);
        } else {
          localStorage.removeItem("refreshToken");
        }
      },
    );

    // Reaction to handle delivery type changes globally
    reaction(
      () => this.isDirectDelivery,
      (isDirect) => {
        if (isDirect !== null) {
          // Refresh products
          store.productStore.searchProducts();
          
          // Refresh cart if customer is selected and we have a warehouse
          const searchParams = new URLSearchParams(window.location.search);
          const activeWarehouseId = this.warehouseId || Number(searchParams.get("warehouseId"));
          const activeCustomerTypeId = this.customerTypeId || Number(searchParams.get("customerTypeId")) || this.customer?.customerTypeId || 0;
          const activeDeliveryType = Number(searchParams.get("deliveryType")) || store.productStore.deliveryType;
          
          if (this.customer?.id && activeWarehouseId) {
            store.cartStore.getCartItems({
              outletId: this.customer.id,
              businessTypeId: activeCustomerTypeId,
              warehouseId: activeWarehouseId,
              order_type: activeDeliveryType,
            });
          }
        }
      }
    );
  }

  setCustomerDetail = async (customerDetail: CustomerDetails) => {
  runInAction(() => {
    this.customer = customerDetail;
    this.warehouseId = null;
    this.availableDeliveryTypes = [];
  });

  try {
    const lat = customerDetail?.shippingLat ? Number(customerDetail.shippingLat) : 0;
    const long = customerDetail?.shippingLong ? Number(customerDetail.shippingLong) : 0;

    if (lat && long && customerDetail?.customerTypeId) {
      const warehouseResponse = await agent.Warehouse.getWarehouseId(
        lat,
        long,
        customerDetail.customerTypeId,
        customerDetail.id,
      );
      runInAction(() => {
        const deliveryTypes =
          warehouseResponse?.data?.[0]?.warehouses?.[0]?.orderDeliveryTypes || [];
        const warehouseId =
          warehouseResponse?.data?.[0]?.warehouses?.[0]?.warehouseId || null;
        this.availableDeliveryTypes = deliveryTypes.length > 0 ? deliveryTypes : (customerDetail?.deliveryTypes || []);
        this.warehouseId = warehouseId;
      });
    } else {
      runInAction(() => {
        this.warehouseId = null;
        this.availableDeliveryTypes = customerDetail?.deliveryTypes || [];
      });
    }
  } catch (error: any) {
    console.error("Error fetching warehouse delivery types:", error);
    runInAction(() => {
      this.warehouseId = null;
      this.availableDeliveryTypes = customerDetail?.deliveryTypes || [];
    });
    const errorMessage = error?.response?.data?.message || "Warehouse not configured";
    store.toasterStore.error(errorMessage);
  }
};

  fetchCustomerDetail = async (id: number) => {
    this.loadingCustomerDetail = true;
    try {
      const response = await agent.Customer.detail(id);
      await this.setCustomerDetail(response.data);
    } catch (error) {
      console.error("Error fetching customer detail:", error);
    } finally {
      runInAction(() => {
        this.loadingCustomerDetail = false;
      });
    }
  };

  fetchWarehouseId = async (lat: number, long: number, customerTypeId: number, outletId: number) => {
    this.loadingWarehouse = true;
    try {
      const warehouse = await agent.Warehouse.getWarehouseId(lat, long, customerTypeId, outletId);
      runInAction(() => {
        this.warehouseId = warehouse?.data?.[0]?.warehouses?.[0]?.warehouseId || null;
        const deliveryTypes =
          warehouse?.data?.[0]?.warehouses?.[0]?.orderDeliveryTypes || [];
        this.availableDeliveryTypes = deliveryTypes.length > 0 ? deliveryTypes : (this.customer?.deliveryTypes || []);
      });
    } catch (error) {
      console.error("Error fetching warehouse:", error);
      runInAction(() => {
        this.warehouseId = null;
        this.availableDeliveryTypes = [];
      });
    } finally {
      runInAction(() => {
        this.loadingWarehouse = false;
      });
    }
  };

  setWarehouseId = (id: number | null) => {
    this.warehouseId = id;
  };

  setToken = (token: string | null) => {
    this.token = token;
    if (token) {
      localStorage.setItem("jwt", token);
    } else {
      localStorage.removeItem("jwt");
    }
  };

  setRefreshToken = (refreshToken: string | null) => {
    this.refreshToken = refreshToken;
    if (refreshToken) {
      localStorage.setItem("refreshToken", refreshToken);
    } else {
      localStorage.removeItem("refreshToken");
    }
  };

  setUserId = (id: number) => {
    localStorage.setItem("id", id.toString());
  };

  setMenuId = (menuId: number) => {
    this.menuId = menuId;
    localStorage.setItem("menuId", String(menuId));
  };

  setUser = (user: SystemUser | null) => {
    this.user = user;
    if (user?.data?.admin?.customerTypeId != null) {
      this.customerTypeId = user.data.admin.customerTypeId;
      // Persist customerTypeId to localStorage
      localStorage.setItem(
        "customerTypeId",
        String(user.data.admin.customerTypeId),
      );
    } else {
      this.customerTypeId = null;
      localStorage.removeItem("customerTypeId");
    }
  };

  setIsDirectDelivery = (value: boolean | null) => {
    this.isDirectDelivery = value;
    if (value !== null) {
      localStorage.setItem("isDirectDelivery", String(value));
    } else {
      localStorage.removeItem("isDirectDelivery");
    }
  };

  clearAuth = () => {
    this.token = null;
    this.refreshToken = null;
    this.user = null;
    this.customer = null;
    this.customerTypeId = null;
    this.warehouseId = null;
    this.availableDeliveryTypes = [];

    localStorage.removeItem("jwt");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("user");
    localStorage.removeItem("customerTypeId");
    localStorage.removeItem("id");
    localStorage.removeItem("isDirectDelivery");
  };

  // PWA Updates
  workbox: any = null; // Using any to avoid importing Workbox type which might cause issues if not available in strict mode, but ideal is 'Workbox'
  updateAvailable: boolean = false;

  setWorkbox = (wb: any) => {
    this.workbox = wb;
  };

  setUpdateAvailable = (value: boolean) => {
    this.updateAvailable = value;
  };

  updateServiceWorker = async () => {
    if (this.workbox) {
      this.workbox.messageSkipWaiting();
    }
  };
}